import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  ensurePathPartyScaffold,
  formatRawConfigForEditor,
  normalizeRawConfigForSave,
} from '../utils/dslPayloadBuilder';
import { parseJsonSample } from '../utils/jsonPathExtractor';
import { parseXlsxSample } from '../utils/xlsxSampleParser';
import { fieldsToConfigText, parseFieldsFromConfigText } from '../utils/fieldsTreeMutations';

export function useConnectorJsonEditor({ connector, version }) {
  const [sampleInputMode, setSampleInputMode] = useState('json');
  const [sampleJsonText, setSampleJsonText] = useState('');
  const [samplePayload, setSamplePayload] = useState(null);
  const [sampleParseError, setSampleParseError] = useState('');
  const [xlsxFileName, setXlsxFileName] = useState('');
  const [configJsonText, setConfigJsonText] = useState('');
  const [configDirty, setConfigDirty] = useState(false);
  const [mappingMode, setMappingMode] = useState('simple');
  const [fieldsTree, setFieldsTree] = useState({});
  const [mappingModeError, setMappingModeError] = useState('');
  const versionId = version?.id ?? version?.version;

  useEffect(() => {
    if (!version?.raw_config) {
      return;
    }

    const target = version.raw_config.target || connector?.document_type;
    const fields = { ...(version.raw_config.fields || {}) };
    ensurePathPartyScaffold(fields, target);

    setConfigJsonText(formatRawConfigForEditor({ ...version.raw_config, fields }));
    setFieldsTree(fields);
    setConfigDirty(false);
    setMappingModeError('');
  }, [versionId, version?.raw_config, connector?.document_type]);

  const applySampleJson = useCallback((text) => {
    setSampleJsonText(text);
    try {
      const parsed = parseJsonSample(text);
      setSamplePayload(parsed);
      setSampleParseError('');

      return true;
    } catch (err) {
      setSampleParseError(err.message);
      setSamplePayload(null);

      return false;
    }
  }, []);

  const applySampleXlsx = useCallback(async (file) => {
    if (!file) {
      return false;
    }

    setXlsxFileName(file.name);
    setSampleParseError('');

    try {
      const arrayBuffer = await file.arrayBuffer();
      const { payload } = parseXlsxSample(arrayBuffer);

      setSamplePayload(payload);
      setSampleJsonText(JSON.stringify(payload, null, 2));

      return true;
    } catch (err) {
      setSampleParseError(err.message);
      setSamplePayload(null);
      setSampleJsonText('');

      return false;
    }
  }, []);

  const handleSampleInputModeChange = useCallback((mode) => {
    setSampleInputMode(mode);
    setSampleParseError('');
    setSamplePayload(null);
    setSampleJsonText('');
    setXlsxFileName('');
  }, []);

  const setConfigText = useCallback((text) => {
    setConfigJsonText(text);
    setConfigDirty(true);
  }, []);

  const updateFieldsTree = useCallback(
    (nextFields) => {
      setFieldsTree(nextFields);
      setConfigJsonText(fieldsToConfigText(nextFields, { connector, existingConfigText: configJsonText }));
      setConfigDirty(true);
      setMappingModeError('');
    },
    [connector, configJsonText],
  );

  const handleMappingModeChange = useCallback(
    (nextMode) => {
      if (nextMode === mappingMode) {
        return true;
      }

      if (nextMode === 'simple') {
        try {
          const parsedFields = parseFieldsFromConfigText(configJsonText);
          setFieldsTree(parsedFields);
          setMappingModeError('');

          setMappingMode(nextMode);

          return true;
        } catch (err) {
          setMappingModeError(err.message || 'Fix JSON errors before switching to Simple mode');

          return false;
        }
      }

      setMappingMode(nextMode);
      setMappingModeError('');

      return true;
    },
    [configJsonText, mappingMode],
  );

  const configValidation = useMemo(() => {
    if (!configJsonText.trim()) {
      return { valid: false, error: 'Mapping config is empty' };
    }

    try {
      const parsed = JSON.parse(configJsonText);
      normalizeRawConfigForSave(parsed, { connector });

      return { valid: true, error: '' };
    } catch (err) {
      return { valid: false, error: err.message };
    }
  }, [configJsonText, connector]);

  const buildRawConfig = useCallback(() => {
    const parsed = JSON.parse(configJsonText);
    const normalized = normalizeRawConfigForSave(parsed, { connector });
    setConfigJsonText(formatRawConfigForEditor(normalized));
    setFieldsTree(normalized.fields || {});
    setConfigDirty(false);

    return normalized;
  }, [configJsonText, connector]);

  const formatConfigJson = useCallback(() => {
    if (!configValidation.valid) {
      return false;
    }

    try {
      const parsed = JSON.parse(configJsonText);
      setConfigJsonText(formatRawConfigForEditor(parsed));
      setFieldsTree(parsed.fields || {});
      setConfigDirty(false);

      return true;
    } catch {
      return false;
    }
  }, [configJsonText, configValidation.valid]);

  const canSave = Boolean(connector?.document_type) && configValidation.valid;

  const canPreview = canSave && samplePayload !== null && !sampleParseError;

  return {
    sampleInputMode,
    setSampleInputMode: handleSampleInputModeChange,
    sampleJsonText,
    setSampleJsonText,
    samplePayload,
    sampleParseError,
    applySampleJson,
    applySampleXlsx,
    xlsxFileName,
    configJsonText,
    setConfigJsonText: setConfigText,
    configParseError: configValidation.error,
    configValidation,
    formatConfigJson,
    buildRawConfig,
    canSave,
    canPreview,
    configDirty,
    mappingMode,
    setMappingMode: handleMappingModeChange,
    mappingModeError,
    fieldsTree,
    updateFieldsTree,
  };
}
